import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PurchaseOrdersTab from "@/components/PurchaseOrdersTab";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Send, ClipboardCheck, Gavel, X, Paperclip, PackageSearch } from "lucide-react";

export const REQ_ST = {
  borrador: { label: "Borrador", color: "#94a3b8" },
  enviada: { label: "Enviada", color: "#22d3ee" },
  en_revision: { label: "En dirección", color: "#c4b5fd" },
  requiere_correccion: { label: "Requiere corrección", color: "#f59e0b" },
  autorizada: { label: "Autorizada", color: "#16a34a" },
  parcialmente_autorizada: { label: "Parcialmente autorizada", color: "#86efac" },
  rechazada: { label: "Rechazada", color: "#dc2626" },
  ordenada: { label: "Ordenada", color: "#38bdf8" },
  parcialmente_recibida: { label: "Parcialmente recibida", color: "#fbbf24" },
  recibida: { label: "Recibida", color: "#4ade80" },
  cerrada: { label: "Cerrada", color: "#64748b" },
};
const PRIORITY = { baja: "#64748b", normal: "#22d3ee", alta: "#f59e0b", urgente: "#ef4444" };

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
    <div className={`panel w-full ${wide ? "max-w-3xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <h3 className="font-bold text-slate-100 text-sm uppercase tracking-widest">{title}</h3>
        <button onClick={onClose} className="btn-ghost p-1" data-testid="modal-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const ReqFormModal = ({ existing, onClose, onSaved }) => {
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [budget, setBudget] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(existing ? {
    project_id: existing.project_id, priority: existing.priority, required_date: existing.required_date,
    purpose: existing.purpose, expected_scope: existing.expected_scope || "", attachments: existing.attachments || [],
  } : { project_id: "", priority: "normal", required_date: todayLocal(), purpose: "", expected_scope: "", attachments: [] });
  const [items, setItems] = useState(existing ? existing.items.map((i) => ({ ...i })) :
    [{ product_id: "", product_name: "", is_new_product: false, quantity: "", unit: "", budget_item_id: "", notes: "" }]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data));
    api.get("/products").then(({ data }) => setProducts(data));
  }, []);
  useEffect(() => {
    if (!form.project_id) { setBudget([]); return; }
    api.get(`/projects/${form.project_id}/budget`, { params: { include_executed: false } })
      .then(({ data }) => setBudget((data.items || []).filter((b) => b.level === "item")))
      .catch(() => setBudget([]));
  }, [form.project_id]);

  const setItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const pickProduct = (idx, pid) => {
    const p = products.find((x) => x.id === pid);
    setItem(idx, p ? { product_id: pid, product_name: p.name, unit: p.unit || "", is_new_product: false }
      : { product_id: "", is_new_product: true });
  };
  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachments: [...f.attachments, data.id] }));
      toast.success("Archivo adjuntado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
  };
  const save = async (submit) => {
    if (!form.project_id) return toast.error("Selecciona la obra");
    const clean = items.filter((i) => i.product_name && i.quantity > 0 && i.unit);
    if (!clean.length) return toast.error("Agrega al menos un material con cantidad y unidad");
    if ((form.purpose || "").trim().length < 3) return toast.error("Describe para qué se usará");
    setSaving(true);
    const payload = {
      ...form,
      items: clean.map((i) => {
        const b = budget.find((x) => x.id === i.budget_item_id);
        return { ...i, quantity: Number(i.quantity), product_id: i.product_id || null,
          budget_item_id: i.budget_item_id || null, budget_code: b?.code || null, budget_desc: b?.description || null };
      }),
    };
    try {
      if (existing) {
        await api.patch(`/requisitions/${existing.id}`, payload);
        if (submit) await api.post(`/requisitions/${existing.id}/submit`, {});
      } else {
        await api.post("/requisitions", { ...payload, submit });
      }
      toast.success(submit ? "Requisición enviada" : "Borrador guardado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={existing ? `Editar ${existing.folio}` : "Nueva requisición"} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select data-testid="req-project" className="input-cyber px-3 py-2 text-sm" value={form.project_id}
            onChange={(e) => setForm({ ...form, project_id: e.target.value })} disabled={!!existing}>
            <option value="">Obra…</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</option>)}
          </select>
          <select data-testid="req-priority" className="input-cyber px-3 py-2 text-sm" value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {Object.keys(PRIORITY).map((p) => <option key={p} value={p}>Prioridad: {p}</option>)}
          </select>
          <input data-testid="req-date" type="date" className="input-cyber px-3 py-2 text-sm" value={form.required_date}
            onChange={(e) => setForm({ ...form, required_date: e.target.value })} />
        </div>
        <textarea data-testid="req-purpose" className="input-cyber px-3 py-2 text-sm w-full" rows={2}
          placeholder="¿Para qué se utilizará el material? *" value={form.purpose}
          onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
        <input data-testid="req-scope" className="input-cyber px-3 py-2 text-sm w-full"
          placeholder="Alcance esperado (ej. 50 bultos de cemento → 120 m² de firme)" value={form.expected_scope}
          onChange={(e) => setForm({ ...form, expected_scope: e.target.value })} />

        <div className="text-[10px] uppercase tracking-widest text-slate-500">Materiales / servicios</div>
        {items.map((it, idx) => (
          <div key={idx} className="panel p-3 space-y-2" data-testid={`req-item-${idx}`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <select className="input-cyber px-2 py-1.5 text-xs md:col-span-2" value={it.product_id || ""}
                data-testid={`req-item-product-${idx}`} onChange={(e) => pickProduct(idx, e.target.value)}>
                <option value="">— Material nuevo (escríbelo abajo) —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
              </select>
              <input className="input-cyber px-2 py-1.5 text-xs" type="number" min="0" step="any" placeholder="Cantidad *"
                data-testid={`req-item-qty-${idx}`} value={it.quantity}
                onChange={(e) => setItem(idx, { quantity: e.target.value })} />
              <input className="input-cyber px-2 py-1.5 text-xs" placeholder="Unidad *" value={it.unit}
                data-testid={`req-item-unit-${idx}`} onChange={(e) => setItem(idx, { unit: e.target.value })} />
            </div>
            {!it.product_id && (
              <input className="input-cyber px-2 py-1.5 text-xs w-full" placeholder="Nombre del material nuevo *"
                data-testid={`req-item-name-${idx}`} value={it.product_name}
                onChange={(e) => setItem(idx, { product_name: e.target.value, is_new_product: true })} />
            )}
            <div className="flex gap-2 items-center">
              <select className="input-cyber px-2 py-1.5 text-xs flex-1" value={it.budget_item_id || ""}
                data-testid={`req-item-budget-${idx}`} onChange={(e) => setItem(idx, { budget_item_id: e.target.value })}>
                <option value="">Partida presupuestal (opcional)…</option>
                {budget.map((b) => <option key={b.id} value={b.id}>{b.code ? `${b.code} · ` : ""}{(b.description || "").slice(0, 70)}</option>)}
              </select>
              {items.length > 1 && (
                <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="btn-ghost p-1.5" style={{ color: "#fca5a5" }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        <button data-testid="req-add-item" onClick={() => setItems([...items, { product_id: "", product_name: "", is_new_product: false, quantity: "", unit: "", budget_item_id: "", notes: "" }])}
          className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>
          <Plus className="w-3 h-3 inline" /> Agregar material
        </button>

        <div className="flex items-center gap-2">
          <label className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest cursor-pointer" style={{ border: "1px solid var(--border)" }}>
            <Paperclip className="w-3 h-3 inline mr-1" /> Adjuntar foto / croquis / PDF
            <input type="file" className="hidden" accept="image/*,application/pdf" data-testid="req-attach"
              onChange={(e) => { if (e.target.files[0]) uploadFile(e.target.files[0]); e.target.value = ""; }} />
          </label>
          {form.attachments.length > 0 && <span className="text-[10px] text-slate-400">{form.attachments.length} adjunto(s)</span>}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button data-testid="req-save-draft" disabled={saving} onClick={() => save(false)}
            className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest" style={{ border: "1px solid var(--border)" }}>Guardar borrador</button>
          <button data-testid="req-save-submit" disabled={saving} onClick={() => save(true)}
            className="btn-primary px-4 py-2 text-xs uppercase tracking-widest">
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <Send className="w-3.5 h-3.5 inline mr-1" />} Enviar a revisión
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ReviewModal = ({ req, onClose, onDone }) => {
  const [checks, setChecks] = useState(null);
  const [notes, setNotes] = useState({ stock_notes: "", budget_notes: "", comment: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/requisitions/${req.id}/checks`).then(({ data }) => setChecks(data)).catch(() => setChecks({}));
  }, [req.id]);
  const act = async (action) => {
    if (action === "correction" && !notes.comment.trim()) return toast.error("Indica qué debe corregirse");
    setBusy(true);
    try {
      if (action === "forward") {
        await api.post(`/requisitions/${req.id}/forward`, { stock_notes: notes.stock_notes || null, budget_notes: notes.budget_notes || null, comment: notes.comment || null });
        toast.success("Enviada a dirección");
      } else {
        await api.post(`/requisitions/${req.id}/request-correction`, { comment: notes.comment });
        toast.success("Corrección solicitada");
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Revisar ${req.folio}`} onClose={onClose} wide>
      {!checks ? <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div> : (
        <div className="space-y-3 text-xs">
          <div className="panel p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Partidas solicitadas · existencias · presupuesto</div>
            {req.items.map((it) => {
              const b = it.budget_item_id ? checks.budget?.[it.budget_item_id] : null;
              const st = it.product_id != null && checks.stock ? checks.stock[it.product_id] : null;
              return (
                <div key={it.id} className="py-1.5 border-b last:border-0 flex items-center gap-3 flex-wrap" style={{ borderColor: "var(--border)" }}>
                  <span className="text-slate-200 flex-1 min-w-0">{it.product_name} — <b>{it.quantity} {it.unit}</b>{it.is_new_product && <span className="ml-1 text-[9px] px-1 rounded" style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}>NUEVO</span>}</span>
                  <span className="font-mono" style={{ color: st > 0 ? "#86efac" : "var(--text-dim)" }}>stock: {st != null ? st : "—"}</span>
                  {b && <span className="font-mono" style={{ color: b.available > 0 ? "#86efac" : "#fca5a5" }}>{b.code}: disp. {fmtMXN(b.available)}</span>}
                </div>
              );
            })}
          </div>
          {checks.duplicates?.length > 0 && (
            <div className="panel p-3" style={{ border: "1px solid #f59e0b" }} data-testid="review-duplicates">
              <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#f59e0b" }}>⚠ Posibles duplicados</div>
              {checks.duplicates.map((d) => (
                <div key={d.id} className="text-slate-300">{d.folio} ({REQ_ST[d.status]?.label}) de {d.created_by_name} — {d.matched.join(", ")}</div>
              ))}
            </div>
          )}
          <input className="input-cyber px-2 py-1.5 w-full" placeholder="Notas de existencias (opcional)" value={notes.stock_notes} onChange={(e) => setNotes({ ...notes, stock_notes: e.target.value })} data-testid="review-stock-notes" />
          <input className="input-cyber px-2 py-1.5 w-full" placeholder="Notas de presupuesto (opcional)" value={notes.budget_notes} onChange={(e) => setNotes({ ...notes, budget_notes: e.target.value })} data-testid="review-budget-notes" />
          <textarea className="input-cyber px-2 py-1.5 w-full" rows={2} placeholder="Comentario / correcciones solicitadas" value={notes.comment} onChange={(e) => setNotes({ ...notes, comment: e.target.value })} data-testid="review-comment" />
          <div className="flex justify-end gap-2">
            <button data-testid="review-correction" disabled={busy} onClick={() => act("correction")}
              className="btn-ghost px-3 py-2 text-[10px] uppercase tracking-widest" style={{ color: "#f59e0b", border: "1px solid #f59e0b" }}>Solicitar corrección</button>
            <button data-testid="review-forward" disabled={busy} onClick={() => act("forward")}
              className="btn-primary px-3 py-2 text-[10px] uppercase tracking-widest">Enviar a dirección</button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const DecideModal = ({ req, onClose, onDone }) => {
  const [decision, setDecision] = useState("autorizada");
  const [reason, setReason] = useState("");
  const [qty, setQty] = useState(Object.fromEntries(req.items.map((i) => [i.id, i.quantity])));
  const [selfOverride, setSelfOverride] = useState(false);
  const { user } = useAuth();
  const isOwn = req.created_by === user?.id;
  const [busy, setBusy] = useState(false);
  const act = async () => {
    if (reason.trim().length < 3) return toast.error("Registra el motivo de la decisión");
    setBusy(true);
    try {
      await api.post(`/requisitions/${req.id}/decide`, {
        decision, reason,
        items: decision === "parcial" ? req.items.map((i) => ({ item_id: i.id, qty_authorized: Number(qty[i.id] || 0) })) : null,
        self_override: selfOverride,
      });
      toast.success("Decisión registrada");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Decidir ${req.folio}`} onClose={onClose} wide>
      <div className="space-y-3 text-xs">
        <div className="panel p-3 space-y-1">
          <div className="text-slate-300"><b>Justificación:</b> {req.purpose}</div>
          {req.expected_scope && <div className="text-slate-400"><b>Alcance:</b> {req.expected_scope}</div>}
          {req.review && (
            <div className="text-slate-400 pt-1 border-t" style={{ borderColor: "var(--border)" }}>
              Revisado por {req.review.reviewed_by}{req.review.stock_notes ? ` · Stock: ${req.review.stock_notes}` : ""}{req.review.budget_notes ? ` · Presupuesto: ${req.review.budget_notes}` : ""}
            </div>
          )}
        </div>
        <div className="flex gap-1 flex-wrap">
          {[["autorizada", "Autorizar todo", "#16a34a"], ["parcial", "Autorizar parcial", "#86efac"], ["devuelta", "Devolver", "#f59e0b"], ["rechazada", "Rechazar", "#dc2626"]].map(([v, l, c]) => (
            <button key={v} data-testid={`decide-${v}`} onClick={() => setDecision(v)}
              className="px-3 py-1.5 text-[10px] uppercase tracking-widest rounded-sm border"
              style={{ borderColor: decision === v ? c : "var(--border)", color: decision === v ? c : "var(--text-dim)", background: decision === v ? `${c}18` : "transparent" }}>{l}</button>
          ))}
        </div>
        {req.items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 py-1 border-b" style={{ borderColor: "var(--border)" }}>
            <span className="flex-1 text-slate-200">{it.product_name} — solicitado <b>{it.quantity} {it.unit}</b></span>
            {decision === "parcial" && (
              <input type="number" min="0" max={it.quantity} step="any" className="input-cyber px-2 py-1 w-24 text-right"
                data-testid={`decide-qty-${it.id}`} value={qty[it.id]} onChange={(e) => setQty({ ...qty, [it.id]: e.target.value })} />
            )}
          </div>
        ))}
        <textarea data-testid="decide-reason" className="input-cyber px-2 py-1.5 w-full" rows={2}
          placeholder="Motivo de la decisión (obligatorio) *" value={reason} onChange={(e) => setReason(e.target.value)} />
        {isOwn && (
          <label className="flex items-center gap-2 text-[11px]" style={{ color: "#f59e0b" }}>
            <input type="checkbox" checked={selfOverride} onChange={(e) => setSelfOverride(e.target.checked)} data-testid="decide-self-override" />
            Auto-autorización expresa de mi propia requisición (queda registrada en auditoría)
          </label>
        )}
        <div className="flex justify-end">
          <button data-testid="decide-confirm" disabled={busy} onClick={act} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <Gavel className="w-3.5 h-3.5 inline mr-1" />} Registrar decisión
          </button>
        </div>
      </div>
    </Modal>
  );
};

const HistoryModal = ({ req, onClose }) => (
  <Modal title={`Historial ${req.folio}`} onClose={onClose}>
    <div className="space-y-1 text-xs">
      {(req.history || []).slice().reverse().map((h, i) => (
        <div key={i} className="py-1.5 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
          <div className="text-slate-200">{h.action} <span className="text-slate-500">→ {REQ_ST[h.to_status]?.label || h.to_status}</span></div>
          <div className="text-[10px] text-slate-500">{new Date(h.at).toLocaleString("es-MX")} · {h.by_name} ({h.by_role}){h.comment ? ` · "${h.comment}"` : ""}</div>
        </div>
      ))}
    </div>
  </Modal>
);

export default function Purchasing() {
  const { user } = useAuth();
  const [tab, setTab] = useState("requisiciones");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [editReq, setEditReq] = useState(null);
  const [reviewReq, setReviewReq] = useState(null);
  const [decideReq, setDecideReq] = useState(null);
  const [histReq, setHistReq] = useState(null);
  const [ocFromReq, setOcFromReq] = useState(null);
  const canReview = ["admin", "contador", "auxiliar"].includes(user?.role);
  const canDecide = user?.role === "admin";

  const load = () => {
    api.get("/requisitions").then(({ data }) => setRows(data))
      .catch(() => toast.error("No se pudieron cargar las requisiciones"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (tab === "requisiciones") load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeAll = () => { setOpenNew(false); setEditReq(null); setReviewReq(null); setDecideReq(null); load(); };
  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar el borrador ${r.folio}?`)) return;
    try { await api.delete(`/requisitions/${r.id}`); toast.success("Borrador eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const closeReq = async (r) => {
    if (!window.confirm(`¿Cerrar la requisición ${r.folio}?`)) return;
    try { await api.post(`/requisitions/${r.id}/close`, {}); toast.success("Requisición cerrada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const visible = filter ? rows.filter((r) => r.status === filter) : rows;

  return (
    <div data-testid="purchasing-page">
      <PageHeader title="Compras" subtitle="Requisiciones y órdenes de compra" testId="purchasing-header"
        actions={tab === "requisiciones" && (
          <button data-testid="new-req-btn" onClick={() => setOpenNew(true)} className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nueva requisición
          </button>
        )} />
      <div className="px-6 lg:px-8 py-6 space-y-4">
        <div className="flex gap-1 flex-wrap items-center">
          {[["requisiciones", "Requisiciones"], ...(canReview ? [["ordenes", "Órdenes de compra"]] : [])].map(([v, l]) => (
            <button key={v} data-testid={`purchasing-tab-${v}`} onClick={() => setTab(v)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm border ${tab === v ? "text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}
              style={{ borderColor: tab === v ? "var(--cyan)" : "var(--border)", background: tab === v ? "rgba(34,211,238,0.07)" : "transparent" }}>{l}</button>
          ))}
          {tab === "requisiciones" && (
            <select data-testid="req-filter" className="input-cyber px-2 py-1 text-xs ml-auto" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">Todos los estados</option>
              {Object.entries(REQ_ST).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
            </select>
          )}
        </div>

        {tab === "ordenes" ? <PurchaseOrdersTab user={user} fromReq={ocFromReq} clearFromReq={() => setOcFromReq(null)} /> : loading ? (
          <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>
        ) : (
          <div className="space-y-2">
            {visible.length === 0 && (
              <div className="panel p-8 text-center text-slate-400 text-sm">
                <PackageSearch className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                Sin requisiciones. El residente crea la solicitud de materiales desde "Nueva requisición".
              </div>
            )}
            {visible.map((r) => {
              const st = REQ_ST[r.status] || REQ_ST.borrador;
              const editable = ["borrador", "requiere_correccion"].includes(r.status);
              return (
                <div key={r.id} className="panel px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`req-${r.id}`}>
                  <span className="font-mono text-sm font-bold text-slate-100">{r.folio}</span>
                  <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" style={{ background: `${st.color}22`, color: st.color, border: `1px solid ${st.color}66` }} data-testid={`req-status-${r.id}`}>{st.label}</span>
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-bold" style={{ color: PRIORITY[r.priority], border: `1px solid ${PRIORITY[r.priority]}66` }}>{r.priority}</span>
                  <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
                    <span className="text-slate-300">{r.project_code ? `${r.project_code} · ` : ""}{r.project_name}</span> · {r.items.length} partida(s) · requiere {fmtDate(r.required_date)} · por {r.created_by_name}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {editable && (r.created_by === user?.id || user?.role === "admin") && (
                      <button data-testid={`req-edit-${r.id}`} onClick={() => setEditReq(r)} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>✎ Editar</button>
                    )}
                    {canReview && r.status === "enviada" && (
                      <button data-testid={`req-review-${r.id}`} onClick={() => setReviewReq(r)} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#c4b5fd", border: "1px solid #7c3aed" }}>
                        <ClipboardCheck className="w-3 h-3 inline" /> Revisar
                      </button>
                    )}
                    {canDecide && (r.status === "en_revision" || (r.status === "enviada" && r.priority === "urgente")) && (
                      <button data-testid={`req-decide-${r.id}`} onClick={() => setDecideReq(r)} className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest">
                        <Gavel className="w-3 h-3 inline" /> Decidir
                      </button>
                    )}
                    {canReview && ["autorizada", "parcialmente_autorizada", "ordenada", "parcialmente_recibida"].includes(r.status) && (
                      <button data-testid={`req-oc-${r.id}`} onClick={() => { setOcFromReq(r); setTab("ordenes"); }} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#86efac", border: "1px solid #16a34a" }}>→ OC</button>
                    )}
                    {["admin", "contador"].includes(user?.role) && ["recibida", "parcialmente_recibida"].includes(r.status) && (
                      <button data-testid={`req-close-${r.id}`} onClick={() => closeReq(r)} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--text-dim)", border: "1px solid var(--border)" }}>Cerrar</button>
                    )}
                    <button data-testid={`req-history-${r.id}`} onClick={() => setHistReq(r)} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--text-dim)", border: "1px solid var(--border)" }}>Historial</button>
                    {r.status === "borrador" && (r.created_by === user?.id || user?.role === "admin") && (
                      <button data-testid={`req-delete-${r.id}`} onClick={() => remove(r)} className="btn-ghost p-1.5" style={{ color: "#fca5a5" }}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                  {r.decision && (
                    <div className="w-full text-[10px] font-mono" style={{ color: r.decision.type === "rechazada" ? "#fca5a5" : "#86efac" }}>
                      ⚖ {r.decision.type} por {r.decision.by} · "{r.decision.reason}"{r.decision.self_authorized ? " · AUTO-AUTORIZADA" : ""}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {openNew && <ReqFormModal onClose={() => setOpenNew(false)} onSaved={closeAll} />}
      {editReq && <ReqFormModal existing={editReq} onClose={() => setEditReq(null)} onSaved={closeAll} />}
      {reviewReq && <ReviewModal req={reviewReq} onClose={() => setReviewReq(null)} onDone={closeAll} />}
      {decideReq && <DecideModal req={decideReq} onClose={() => setDecideReq(null)} onDone={closeAll} />}
      {histReq && <HistoryModal req={histReq} onClose={() => setHistReq(null)} />}
    </div>
  );
}
